import { computed, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { storeToRefs } from 'pinia'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useAnchorPageStore } from '../anchor_page.store'
import type { AnchorObjective } from '../anchor_page_types'
import type { GeneratedAssessment } from '../assessments/generated_assessment_types'
import { useGeneratedAssessmentsStore } from '../assessments/generated_assessments.store'
import { generationBlockedReason } from '../assessments/generation_eligibility'
import { annualGenerationTarget, objectiveGenerationTarget, type GenerationTarget } from '../assessments/generation_targets'
import { useAssessmentGeneration } from '../assessments/useAssessmentGeneration'
import type { ObjectiveLevel } from '../objectives/objective_levels'

function logFetchFailure(error: unknown) {
  console.error('fetchGeneratedAssessments', error)
}

/**
 * A saved anchor page's generated assessments and its Generate entry points, fetched as soon as the
 * copy has loaded. Every fetch here is non-fatal: the strip is supplementary, so a failure costs the
 * strip, never the page.
 */
export function useSavedAnchorAssessments(
  savedPageUuid: MaybeRefOrGetter<string>,
  isPageLoaded: MaybeRefOrGetter<boolean>,
  analytics: AnalyticsTracker
) {
  const anchorPageStore = useAnchorPageStore()
  const { content, customPrimaryGoalText, customObjectives, isFetching, actionPermissions } = storeToRefs(anchorPageStore)
  const generatedAssessmentsStore = useGeneratedAssessmentsStore()
  const { generateAssessmentVisibility, betaVisibility } = storeToRefs(useVisibilityStore())

  const isGenerateBlockedModalOpen = ref(false)

  function refreshGeneratedAssessments() {
    return generatedAssessmentsStore.fetchGeneratedAssessments(toValue(savedPageUuid))
  }

  const generation = useAssessmentGeneration({
    savedPageUuid,
    goalUuid: () => content.value?.uuid ?? '',
    grades: () => content.value?.grades,
    analytics,
    refreshGeneratedAssessments
  })

  // Access is asked every time, as it can be revoked; the list is fetched once per page, as the store keeps it.
  async function fetchAssessments() {
    if (!generateAssessmentVisibility.value) return
    const uuid = toValue(savedPageUuid)
    const listFetch = generatedAssessmentsStore.isLoadedFor(uuid) ? undefined : refreshGeneratedAssessments().catch(logFetchFailure)
    await Promise.all([listFetch, generatedAssessmentsStore.fetchGenerateAccess(uuid)])
  }

  // Fetches once the copy has loaded with the flag on, whichever of the two comes last.
  watch([generateAssessmentVisibility, () => toValue(isPageLoaded)], ([isVisible, isLoaded]) => {
    if (isVisible && isLoaded) void fetchAssessments()
  }, { immediate: true })

  const isEntryPointShown = computed(
    () => !isFetching.value && generateAssessmentVisibility.value && generatedAssessmentsStore.isGenerateAllowedFor(toValue(savedPageUuid))
  )

  // Beta lifts the content gates so staff can generate against any goal; it never shows a hidden tile.
  const blockedReason = computed(() => (betaVisibility.value ? null : generationBlockedReason(content.value)))

  const annualAssessments = computed<GeneratedAssessment[]>(() => {
    if (!generateAssessmentVisibility.value) return []
    return generatedAssessmentsStore.annualTargetFor(toValue(savedPageUuid))?.assessments ?? []
  })

  function objectiveAssessments(level: ObjectiveLevel): GeneratedAssessment[] {
    if (!generateAssessmentVisibility.value) return []
    return generatedAssessmentsStore.objectiveTargetFor(toValue(savedPageUuid), level)?.assessments ?? []
  }

  const levelsWithGeneratedAssessments = computed<ObjectiveLevel[]>(() => {
    const levels = (content.value?.objectives ?? []).map((objective) => objective.level)
    if (isEntryPointShown.value) return levels
    return levels.filter((level) => objectiveAssessments(level).length > 0)
  })

  const hasAnnualAssessments = computed(() => annualAssessments.value.length > 0 || isEntryPointShown.value)

  // Every entry point lands here. A paywall for the UI only: the generate route authorizes on ownership.
  function requestGeneration(target: GenerationTarget) {
    if (actionPermissions.value.generateAssessment.block) {
      isGenerateBlockedModalOpen.value = true
      return
    }
    generation.openModal(target)
  }

  function requestAnnualGeneration() {
    requestGeneration(annualGenerationTarget(customPrimaryGoalText.value))
  }

  function requestObjectiveGeneration(objective: AnchorObjective, index: number) {
    requestGeneration(objectiveGenerationTarget(objective.level, customObjectives.value[index]?.text, objective.text))
  }

  function removeAssessment(uuid: string) {
    generatedAssessmentsStore.removeGeneratedAssessment(toValue(savedPageUuid), uuid)
  }

  return {
    generation,
    isGenerateBlockedModalOpen,
    isEntryPointShown,
    blockedReason,
    annualAssessments,
    hasAnnualAssessments,
    objectiveAssessments,
    levelsWithGeneratedAssessments,
    requestAnnualGeneration,
    requestObjectiveGeneration,
    removeAssessment
  }
}
